/**
 * This file is made for formatting data from the OneSearch request to a format our propertyCard expects
 */

import { Image } from '@awaze/design-system.compositions.image-carousel-overlay'
import {
  Deposit,
  getActiveDepositScheme,
} from '@awaze/mfe-common.configs.brand'
import { AccommodationType } from '@awaze/mfe-common.types-and-mocks.site-search-service'
import { UniqueSellingPoint } from '@awaze/mfe-common.types-and-mocks.site-search-service/types/properties.types'
import { formatPropertyPageURL } from '@awaze/mfe-common.utils.pdp-url'

import {
  DEFAULT_IMAGE_URL,
  IMAGE_DOMAIN_BASE_URL,
  IMAGE_DOMAIN_NOVASOL,
  PRICE_FROM_NIGHTS,
} from 'src/constants'
import { getEndDateFromStartDateDuration } from 'src/utils/getEndDateFromStartDateDuration'

import { isNovasolPoweredBrand } from '../../utils/isNovasolPoweredBrand'

import { removeFloorplanImagesFromImageArray } from './helpers'
import type {
  Brand,
  PropertyDetailLinkProps,
  PropertyFormatterProps,
  PropertyFormatterReturnProps,
  PropertyImageOSProps,
  SetActiveDepositSchemeProps,
} from './propertyFormatter.types'

export const getDecodedText = (text: string | undefined): string | null => {
  if (text === undefined || !text) return null

  const parser = new DOMParser()
  const doc = parser.parseFromString(text, 'text/html')
  const returnString = doc.documentElement.textContent

  if (returnString === null) return null

  return returnString
}

export const getParsedNumber = (
  value: string | number | undefined,
): number | null => {
  if (value === undefined || !value) return null
  const parsedNumber = parseFloat(value.toString())
  if (isNaN(parsedNumber)) {
    return null
  }
  return parsedNumber
}

export const getFormattedDate = (date: string | undefined): Date | null => {
  if (date === undefined || !date) return null

  const dateInstance = new Date(date)

  if (isNaN(dateInstance.getTime())) return null

  return dateInstance
}

export const setImageAttributes = (
  images: PropertyImageOSProps[] | undefined,
  brand: Brand,
): Array<Image> => {
  if (images === undefined || images.length === 0)
    return [
      {
        src: DEFAULT_IMAGE_URL,
        alt: getDecodedText('Image coming soon') ?? '',
      },
    ]

  const imagesWithoutFloorplans = images
    ? removeFloorplanImagesFromImageArray(images)
    : []

  const formattedImageArray = imagesWithoutFloorplans.map(
    (image: PropertyImageOSProps) => {
      let imageURI

      if (isNovasolPoweredBrand(brand)) {
        const propertySuffix = image.assetId.split('/')[1]
        imageURI = `${IMAGE_DOMAIN_NOVASOL}/pic/600/${propertySuffix.substring(0, 3)}/${propertySuffix}`
      } else {
        imageURI = `${IMAGE_DOMAIN_BASE_URL}/${image.assetId.substring(0, 3)}/640/${image.assetId}.jpg`
      }

      return {
        src: imageURI,
        alt: getDecodedText(image.caption) ?? image.caption,
      }
    },
  )

  return formattedImageArray
}

export const getArrayValues = <T>(
  values: Array<T> | undefined,
): Array<T> | null => {
  if (!values?.length) return null

  return values
}

export const setActiveDepositScheme = ({
  cardinality,
  price,
  priceFrom,
  sleeps,
  startDate,
  country,
  accommodationType,
  deposits,
  lengthOfStay,
}: SetActiveDepositSchemeProps): Deposit | null => {
  if (startDate === null || deposits === null) return null

  let priceForActiveDepositScheme

  if (cardinality === 'SINGLE' && price) {
    priceForActiveDepositScheme = price
  } else {
    priceForActiveDepositScheme = priceFrom
  }

  const sleepsForActiveDepositScheme =
    cardinality === 'SINGLE' && sleeps ? sleeps : 0

  const dateToday = new Date()
  dateToday.setUTCHours(0, 0, 0, 0)

  const startDateInst = new Date(startDate)
  const endDateInst = getEndDateFromStartDateDuration(
    startDateInst,
    lengthOfStay,
  )
  const normalizedCountry = country?.toUpperCase()

  const activeDepositScheme =
    startDate &&
    getActiveDepositScheme(
      deposits,
      dateToday,
      startDateInst,
      endDateInst,
      sleepsForActiveDepositScheme,
      priceForActiveDepositScheme ?? 0,
      accommodationType,
      normalizedCountry,
    )

  if (activeDepositScheme) {
    return activeDepositScheme
  } else {
    return null
  }
}

export const setPropertyDetailLink = ({
  lengthOfStay,
  title,
  code,
  siteName,
  siteCode,
  pdpUrlSegment,
  searchParameters,
  startDate,
}: PropertyDetailLinkProps): string => {
  lengthOfStay && searchParameters.set('nights', lengthOfStay.toString())

  if (startDate) {
    const day = String(startDate.getDate()).padStart(2, '0')
    const month = String(startDate.getMonth() + 1).padStart(2, '0')
    const year = String(startDate.getFullYear())
    searchParameters.set('start', `${day}-${month}-${year}`)
  }

  const formattedParams = searchParameters.toString().replace(/%21/g, '') // HACK: Remove ! (ideally should be fixed in fusion)

  const pageURL = formatPropertyPageURL(
    pdpUrlSegment,
    title || '',
    code,
    formattedParams,
    siteName,
    siteCode,
  )

  return pageURL
}

const setExperimentsValues = ({
  formattedProps,
  searchParams,
  datedSearch,
  brand,
}: {
  formattedProps: PropertyFormatterReturnProps
  searchParams: URLSearchParams
  datedSearch: boolean
  brand: Brand
}) => {
  if (!datedSearch && brand !== 'novasol') {
    formattedProps.wasPrice = null
    formattedProps.startDate = null
  }

  if (
    searchParams.get('accommodationType') === 'holiday-parks' ||
    searchParams.get('accommodationType') === 'lodges'
  ) {
    formattedProps.startDate = null
  }

  return formattedProps
}

interface HandleRh3AndPropertyLocationParams {
  rhs3: string | undefined
  propertyLocation: string | undefined
}

const handleRh3AndPropertyLocation = ({
  rhs3,
  propertyLocation,
}: HandleRh3AndPropertyLocationParams) => {
  let locationText: string = ''

  if (rhs3) locationText += `${getDecodedText(rhs3)} `
  if (propertyLocation) locationText += getDecodedText(propertyLocation)

  return locationText
}

const getKSPArray = (KSPs: Array<UniqueSellingPoint>): string[] => {
  const kspArray: string[] = []

  KSPs.forEach((ksp) => {
    const decodedKSP = getDecodedText(ksp.value)
    if (decodedKSP) {
      kspArray.push(decodedKSP)
    }
  })

  return kspArray
}

export const formatter = ({
  property,
  brand,
  deposits,
  searchParams,
  datedSearch,
  pdpUrlSegment,
  country,
  formatDate,
}: PropertyFormatterProps): PropertyFormatterReturnProps => {
  const accommodationType = searchParams.get('accommodationType') || 'accom'

  const getDateStringFormatted = (date: string | undefined): string | null => {
    if (!date) {
      return null
    }

    const UTCDate = new Date(`${date}T00:00:00`)

    return `${formatDate(UTCDate, { day: 'numeric' })} ${formatDate(UTCDate, {
      month: 'short',
    })}`
  }

  const isNovasolBrand = isNovasolPoweredBrand(brand)

  let propertyName = getDecodedText(property.displayName)
  let propertyLocation = handleRh3AndPropertyLocation({
    rhs3: property.rhs3,
    propertyLocation: property.location,
  })

  if (isNovasolBrand) {
    const currentPropertyName = propertyName
    const currentPropertyLocation = propertyLocation
    propertyName = currentPropertyLocation || null
    propertyLocation = currentPropertyName || ''
  }

  const formattedPropertyCardProps: PropertyFormatterReturnProps = {
    brand: brand,
    code: property.code,
    propertyName,
    propertyLocation,
    images: setImageAttributes(property.images, brand),
    price: getParsedNumber(property.price),
    fromPrice: getParsedNumber(property.priceFrom),
    wasPrice: getParsedNumber(property.wasPrice),
    lengthOfStay: getParsedNumber(property.lengthOfStay) ?? PRICE_FROM_NIGHTS,
    pageUrl: setPropertyDetailLink({
      startDate: datedSearch ? getFormattedDate(property.startDate) : null,
      lengthOfStay: getParsedNumber(property.lengthOfStay),
      title: getDecodedText(property.displayName),
      code: property.code,
      siteName: '',
      siteCode: '',
      pdpUrlSegment: pdpUrlSegment,
      searchParameters: searchParams,
    }),
    hasPetsFree: property.petsGoFree ?? false,
    hasCallout: property.isCustomerChoice ?? false,
    activeDepositScheme: setActiveDepositScheme({
      cardinality: property.cardinality,
      price: getParsedNumber(property.price),
      priceFrom: getParsedNumber(property.priceFrom),
      sleeps: getParsedNumber(property.property?.sleeps),
      deposits: deposits || null,
      startDate: datedSearch ? getFormattedDate(property.startDate) : null,
      lengthOfStay: getParsedNumber(property.lengthOfStay) ?? 0,
      country,
      accommodationType: accommodationType as AccommodationType,
    }),
    property: {
      pets: getParsedNumber(property.property?.pets),
      sleeps: getParsedNumber(property.property?.sleeps),
      bedrooms: getParsedNumber(property.property?.bedrooms),
      bathrooms: getParsedNumber(property.property?.bathrooms),
      // TODO: These extras currently display as pure pills without mentioning "incl." as on the designs eg "Bed linen" instead of "Bed linen incl."
      includedExtras: getArrayValues(
        property.property?.includedExtras?.map(
          (extra) => extra.includedExtraName,
        ),
      ),
    },
    keySellingPoints: getKSPArray(property.USPs || []),
    startDate: getDateStringFormatted(property.startDate),
    guestRating: getParsedNumber(property.averageReviewScore),
    grade: getParsedNumber(property.grade),
    urgencyMessaging: !!property.urgencyNudgeMessaging,
  }

  const returnedProperty = setExperimentsValues({
    formattedProps: formattedPropertyCardProps,
    searchParams: searchParams,
    datedSearch: datedSearch,
    brand: brand,
  })

  return returnedProperty
}
